import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { paymentsApi } from "@/lib/api";
import { PageShell } from "./Overview";
import { CheckCircle2, XCircle, Loader } from "lucide-react";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [tx, setTx] = useState(null);
  const nav = useNavigate();

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const res = await paymentsApi.status(sessionId);
      setTx(res);
      if (res.payment_status === "paid") { setState("paid"); return; }
      if (res.status === "expired" || res.payment_status === "expired") { setState("expired"); return; }
      if (attempt >= 8) { setState("timeout"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setState("error");
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <PageShell title="Payment" subtitle="Stripe checkout">
      <div className="max-w-xl border border-border-subtle bg-surface-card p-10 text-center">
        {state === "checking" && (
          <>
            <Loader size={36} className="mx-auto text-artha-amber animate-spin" />
            <div className="h-display text-3xl mt-6">Confirming payment…</div>
            <p className="text-sm text-ink-muted mt-3">Hold tight, we're verifying with Stripe.</p>
          </>
        )}
        {state === "paid" && tx && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-artha-amber" />
            <div className="h-display text-4xl mt-6">Payment received.</div>
            <p className="text-sm text-ink-muted mt-3">
              ${Number(tx.amount).toFixed(2)} {tx.currency?.toUpperCase()} · {tx.purpose === "entity_registration" ? "Entity registration" : "Subscription"}
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              {tx.entity_id ? (
                <Link to={`/app/entities/${tx.entity_id}`} data-testid="success-view-entity"
                  className="bg-artha-amber text-artha-ink px-5 py-3 text-sm hover:bg-artha-cream transition-colors">View entity</Link>
              ) : (
                <Link to="/app/dashboard" className="bg-artha-amber text-artha-ink px-5 py-3 text-sm">Back to dashboard</Link>
              )}
            </div>
          </>
        )}
        {(state === "expired" || state === "error" || state === "timeout") && (
          <>
            <XCircle size={40} className="mx-auto text-[#C94F4F]" />
            <div className="h-display text-3xl mt-6">Payment not completed.</div>
            <p className="text-sm text-ink-muted mt-3">You can retry from the Payments page or your entity detail.</p>
            <button onClick={() => nav("/app/payments")} data-testid="success-retry"
              className="mt-6 bg-artha-amber text-artha-ink px-5 py-3 text-sm">Go to payments</button>
          </>
        )}
      </div>
    </PageShell>
  );
};

export default PaymentSuccess;
